import { Injectable } from '@angular/core';

import { SECTION_TYPE_KPI_SET, SECTION_TYPE_VISUALS } from 'app/report-editor/report-editor.constants';
import { Guid } from 'app/shared/guid-generator/guid-generator';

import {
    ChartTemplateElementDto,
    ConsumptionTableElement,
    ConsumptionTableTemplateElementAggregatedDto,
    ElementSort,
    ElementType,
    HeaderElement,
    HeaderTemplateElementDto,
    KpiSetElement,
    KpiSetsElementDto,
    LineChartElement,
    ReportTemplateDto,
    ScatterPlotChartElement,
    ScatterPlotElementDto,
    SectionTemplateDto,
    TableElement,
    TableTemplateElementAggregatedDto,
    TemplateElement,
    TemplateSection,
} from '../model';

@Injectable()
export class DtoToTemplateHelperService {
    /**
     * format template sections, to render sections in ui, based on a template dto.
     * @param {ReportTemplateDto} templateDto
     */
    formatUISections(templateDto: ReportTemplateDto): TemplateSection[] {
        const reportEditionUISections: TemplateSection[] = [];
        templateDto.sections.forEach((section: SectionTemplateDto) => {
            reportEditionUISections.push(...this.formatReportEditorSections(section));
        });
        return reportEditionUISections;
    }

    /**
     * A formatted section to render in ui
     * @param {SectionTemplateDto} section : section defined in dto of template
     */
    private formatReportEditorSections(section: SectionTemplateDto): TemplateSection[] {
        const hasKpiSetSection = section?.kpiSets !== null && section?.kpiSets.length > 0;

        const visualElements: TemplateElement[] = []
            .concat(
                this.formatTableElementsInSection(section.tables),
                this.formatConsumptionTableElementsInSection(section.consumptionTables),
                this.formatLineChartElementsInSection(section.charts),
                this.formatHeaderElementsInSection(section.headers),
                this.formatScatterPlotChartElementsInSection(section.scatterPlotCharts),
            )
            .sort(this.sortElementsInSection(section.sorting));

        const visualSection: TemplateSection = {
            id: section.id,
            sortId: section.sortId,
            title: SECTION_TYPE_VISUALS,
            elements: visualElements,
        };

        if (hasKpiSetSection) {
            const kpiSetSection: TemplateSection = {
                id: Guid.newGuid(),
                sortId: section.sortId,
                title: SECTION_TYPE_KPI_SET,
                elements: this.formatKpiSetsElementsInSection(section.kpiSets),
            };

            return [kpiSetSection, visualSection];
        } else {
            return [visualSection];
        }
    }

    /**
     * Sort all items in the sorting array from report template dto
     * @param {ElementSort[]} sorting : sort array from report template dto
     */
    private sortElementsInSection(
        sorting: ElementSort[],
    ): (a: TemplateElement, b: TemplateElement) => number {
        return (elementA: TemplateElement, elementB: TemplateElement) => {
            const sortIdA = this.getSortId(elementA.id, sorting);
            const sortIdB = this.getSortId(elementB.id, sorting);
            return sortIdA > sortIdB ? 1 : -1;
        };
    }

    /**
     * Format tables from dto to table elements which could be rendered in ui
     * @param {SectionTemplateDto} section : section from template dto
     */
    private formatTableElementsInSection(tables: TableTemplateElementAggregatedDto[]): TableElement[] {
        return tables.map(
            (tableItem: TableTemplateElementAggregatedDto) =>
                new TableElement(
                    {
                        id: tableItem.id,
                        title: tableItem.title,
                        type: ElementType.Table,
                    },
                    tableItem.description,
                    tableItem.datapoints,
                    tableItem.granularity,
                ),
        );
    }

    private formatConsumptionTableElementsInSection(
        tables: ConsumptionTableTemplateElementAggregatedDto[],
    ): ConsumptionTableElement[] {
        return tables.map(
            (tableItem: ConsumptionTableTemplateElementAggregatedDto) =>
                new ConsumptionTableElement(
                    {
                        id: tableItem.id,
                        title: tableItem.title,
                        type: ElementType.ConsumptionTable,
                    },
                    tableItem.description,
                    tableItem.datapoints,
                    tableItem.granularity,
                ),
        );
    }

    /**
     * Format linecharts from dto to chart elements which could be rendered in ui
     * @param {SectionTemplateDto} section : section from template dto
     */
    private formatLineChartElementsInSection(charts: ChartTemplateElementDto[]): LineChartElement[] {
        return charts.map(
            (lineChart: ChartTemplateElementDto) =>
                new LineChartElement(
                    {
                        id: lineChart.id,
                        title: lineChart.title,
                        type: ElementType.LineChart,
                    },
                    lineChart.description,
                    lineChart.datapoints,
                    lineChart.legend,
                    lineChart.kpi,
                    lineChart.inOperationFilterType,
                    lineChart.datapointFilters,
                ),
        );
    }

    /**
     * Format scatter  plot from dto to chart elements which could be rendered in ui
     * @param {SectionTemplateDto} charts : section from template dto
     */
    private formatScatterPlotChartElementsInSection(
        charts: ScatterPlotElementDto[],
    ): ScatterPlotChartElement[] {
        return charts.map(
            (chart: ScatterPlotElementDto) =>
                new ScatterPlotChartElement(
                    {
                        id: chart.id,
                        title: chart.title,
                        type: ElementType.ScatterPlot,
                    },
                    chart.description,
                    chart.datapoints,
                    chart.kpi,
                    chart.legend,
                    chart.inOperationFilterType,
                    chart.datapointFilters,
                ),
        );
    }

    /**
     * Format KPI set elements from dto to KPI set elements which could be rendered in the UI.
     * @param {SectionTemplateDto} kpiSets : section from template dto.
     */
    private formatKpiSetsElementsInSection(kpiSets: KpiSetsElementDto[]): KpiSetElement[] {
        return kpiSets.map(
            (kpiSet: KpiSetsElementDto) =>
                new KpiSetElement(
                    {
                        id: kpiSet.id,
                        title: kpiSet.name,
                        type: ElementType.KpiSet,
                    },
                    kpiSet.linkedVisuals,
                ),
        );
    }

    /**
     * Format headers from dto to header elements which could be rendered in ui
     * @param {SectionTemplateDto} section : section from template dto
     */
    private formatHeaderElementsInSection(headers: HeaderTemplateElementDto[]): HeaderElement[] {
        return headers.map(
            (headerItem: HeaderTemplateElementDto) =>
                new HeaderElement({
                    id: headerItem.id,
                    title: headerItem.title,
                    type: ElementType.Header,
                }),
        );
    }

    /**
     * Get sort id of the template element which will be rendered in ui
     * @param {string} elementId : identifier of formatted template element
     * @param {ElementSort[]} sorting : sorting array from template dto
     */
    private getSortId(elementId: string, sorting: ElementSort[]): number {
        const sortId = sorting.find((element: ElementSort) => element.elementId === elementId);
        return sortId ? sortId.sortId : null;
    }
}
